import { isSameValue as sameId } from "./utils";

const getFrameworkLabel = (framework) =>
  framework?.framework_name || framework?.name || framework?.label || String(framework?.id ?? "");

const getCategoryLabel = (category) =>
  category?.name || category?.category_name || category?.label || String(category?.id ?? category?.category_id ?? "");

const getMilestoneId = (milestone) => milestone?.milestone_id ?? milestone?.id;

export const getFrameworkLetters = (name) => {
  if (!name) return "";
  const trimmed = name.trim();
  if (trimmed.startsWith("[")) {
    const match = trimmed.match(/^\[([^\]]+)\]/);
    return match ? match[1].toUpperCase() : trimmed.substring(0, 3).toUpperCase();
  }
  return trimmed.substring(0, 3).toUpperCase();
};

/**
 * Take the first 3 alphanumeric characters of a string (upper-cased),
 * ignoring punctuation, spaces, brackets, etc.
 *
 * @param {string} name
 * @returns {string}
 */
export const getAlphaNumFrameworkCode = (name) => {
  return name
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, 3);
};

const getMilestoneLabel = (milestone) =>
  milestone?.name || milestone?.milestone_name || String(getMilestoneId(milestone) ?? "");

/**
 * Normalize framework-related filter config so callers can support:
 * - legacy shape: filterConfig.frameworks[]
 * - new shape: filterConfig.categories[] where each category has frameworks[]
 */
export const normalizeFrameworkModel = (filterConfig) => {
  if (!filterConfig) {
    return {
      flatFrameworks: [],
      categories: [],
      hasCategories: false,
      frameworkById: new Map(),
      milestoneToFrameworkId: new Map(),
      frameworkIdsByCategoryId: new Map(),
    };
  }

  const frameworkById = new Map();
  const milestoneToFrameworkId = new Map();
  const frameworkIdsByCategoryId = new Map();

  const rawCategories = Array.isArray(filterConfig.categories) ? filterConfig.categories : [];
  const hasCategories = rawCategories.length > 0;
  const normalizedCategories = [];

  const addFramework = (framework, categoryId = null) => {
    const frameworkId = framework?.id;
    if (frameworkId == null) return null;

    const key = String(frameworkId);
    const existing = frameworkById.get(key);
    if (existing) {
      if (Array.isArray(framework?.milestones) && framework.milestones.length > 0 && (!existing.milestones || existing.milestones.length === 0)) {
        existing.milestones = framework.milestones;
      }
      return existing;
    }

    const normalized = {
      ...framework,
      id: frameworkId,
      framework_name: framework?.framework_name || framework?.name,
      milestones: Array.isArray(framework?.milestones) ? framework.milestones : [],
    };
    frameworkById.set(key, normalized);

    if (categoryId != null) {
      const categoryKey = String(categoryId);
      const current = frameworkIdsByCategoryId.get(categoryKey) || [];
      if (!current.some((id) => sameId(id, frameworkId))) {
        frameworkIdsByCategoryId.set(categoryKey, [...current, frameworkId]);
      }
    }

    normalized.milestones.forEach((milestone) => {
      const milestoneId = getMilestoneId(milestone);
      if (milestoneId != null) {
        milestoneToFrameworkId.set(String(milestoneId), frameworkId);
      }
    });

    return normalized;
  };

  if (hasCategories) {
    rawCategories.forEach((category, index) => {
      const categoryId = category?.id ?? category?.category_id ?? `category_${index}`;
      const categoryLabel = getCategoryLabel(category);
      const categoryFrameworks = Array.isArray(category?.frameworks) ? category.frameworks : [];

      const frameworks = categoryFrameworks
        .map((framework) => addFramework(framework, categoryId))
        .filter(Boolean);

      normalizedCategories.push({
        ...category,
        id: categoryId,
        name: categoryLabel,
        frameworks,
      });
    });
  }

  const topLevelFrameworks = Array.isArray(filterConfig.frameworks) ? filterConfig.frameworks : [];
  topLevelFrameworks.forEach((framework) => {
    addFramework(framework);
  });

  const flatFrameworks = Array.from(frameworkById.values());

  return {
    flatFrameworks,
    categories: normalizedCategories,
    hasCategories,
    frameworkById,
    milestoneToFrameworkId,
    frameworkIdsByCategoryId,
  };
};

export const getFrameworksFromFilterConfig = (filterConfig) =>
  normalizeFrameworkModel(filterConfig).flatFrameworks;

export const getMilestoneFrameworkPair = (filterConfig, milestoneId) => {
  if (milestoneId == null) return null;
  const { milestoneToFrameworkId, frameworkById } = normalizeFrameworkModel(filterConfig);
  const frameworkId = milestoneToFrameworkId.get(String(milestoneId));
  if (frameworkId == null) return null;
  const framework = frameworkById.get(String(frameworkId));
  if (!framework) return null;
  const milestone = (framework.milestones || []).find((m) => sameId(getMilestoneId(m), milestoneId));
  return milestone ? { framework, milestone } : null;
};

export { getFrameworkLabel, getCategoryLabel, getMilestoneId, getMilestoneLabel };
